import React, { forwardRef } from 'react';
import Proptypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '../../lib/styles';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    'justify-content': 'center',
    'align-items': 'center',
    width: ({ size }) => {
      return size === 'small' ? 14 : 24;
    },
    height: ({ size }) => {
      return size === 'small' ? 14 : 24;
    },
    padding: ({ size }) => {
      return size === 'small' ? 0 : 3;
    },

    margin: 0,
    color: theme.palette.common.white,
    'font-size': ({ size }) => (size === 'small' ? '0.5125rem' : '12px'),
    'text-align': 'center',
    'border-width': 1,
    'border-style': 'solid',
    'border-color': ({ backgroundColor, borderColor }) => borderColor || backgroundColor,
    'border-radius': '50%',
    'background-color': ({ backgroundColor }) => backgroundColor,
  },
  image: {
    'border-radius': '50%',
    'background-image': ({ src }) => `url(${src})`,
    'background-repeat': 'no-repeat',
    'background-position': 'center',
    'background-size': 'contain',
    width: '100%',
    height: '100%',
  },
  adornmentStyle: {
    position: 'absolute',
    bottom: -3,
    right: -10,
  },
}));

function Adornment({ size, adornment, className }) {
  if (!adornment) return null;
  if (size === 'small') return null;
  return <div className={clsx(className, 'adornment')}>{adornment}</div>;
}

Adornment.propTypes = {
  size: Proptypes.oneOf(['small', 'large']),
  adornment: Proptypes.node,
  className: Proptypes.string,
};

Adornment.defaultProps = {
  size: 'large',
  adornment: null,
  className: null,
};

const Avatar = forwardRef(function Avatar({
  className,
  children,
  src,
  size = 'large',
  adornment,
  backgroundColor,
  borderColor,
}) {
  const { root, image, adornmentStyle } = useStyles({ src, size, backgroundColor, borderColor });

  if (!src) {
    return (
      <div className={clsx(root, className)}>
        {children}
        <Adornment className={clsx(adornmentStyle)} size={size} adornment={adornment} />
      </div>
    );
  }

  return (
    <div className={clsx(root, className)}>
      <div className={clsx(image, 'image')} />
      <Adornment className={clsx(adornmentStyle)} size={size} adornment={adornment} />
    </div>
  );
});

Avatar.propTypes = {
  className: Proptypes.string,
  children: Proptypes.element,
  src: Proptypes.string,
  backgroundColor: Proptypes.string,
  borderColor: Proptypes.string,
  adornment: Proptypes.element,
  size: Proptypes.oneOf(['small', 'large']),
};

Avatar.defaultProps = {
  className: null,
  children: null,
  src: null,
  backgroundColor: null,
  borderColor: null,
  adornment: null,
  size: 'large',
};

Avatar.displayName = 'Avatar';

export default Avatar;
